import { useEffect, useState } from 'react';
import type { Notification } from '../lib/notifications/types';
import {
  subscribeNotifications,
  markNotificationReadForRecipient,
} from '../lib/notifications/firestore';

export type UseNotificationsResult = {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: Error | null;
  markAsRead: (notification: Notification) => Promise<void>;
};

const LIMIT = 20;

export function useNotifications(recipientUserId: string | null): UseNotificationsResult {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!recipientUserId) {
      setNotifications([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const unsub = subscribeNotifications(
      recipientUserId,
      (list) => {
        setNotifications(list);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
        console.warn('[useNotifications] subscribe failed', err);
      },
      { limitCount: LIMIT }
    );
    return () => unsub();
  }, [recipientUserId]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAsRead = async (notification: Notification) => {
    try {
      await markNotificationReadForRecipient({
        recipientUserId: notification.recipientUserId,
        notificationId: notification.id,
      });
    } catch (err) {
      console.warn('[useNotifications] markAsRead failed', notification, err);
    }
  };

  return { notifications, unreadCount, loading, error, markAsRead };
}
